import { useCallback, useEffect, useMemo, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import useOffers from 'api/useOffers';
import { EPermissionAction, EPermissionGroup } from 'common/permissions.types';
import { useUserPermissions } from 'hooks/useUserPermissions';
import { EToasterStatus, showToast } from 'utils/showToast';

type OffersHookReturn = ReturnType<typeof useOffers>;

interface IUseProgressBarsActionsProps {
  deleteOffer: OffersHookReturn['deleteOffer'];
  toggleOfferStatus: OffersHookReturn['toggleOfferStatus'];
  refetch: () => void;
}

interface IUseProgressBarsActionsReturn {
  isManageEnabled: boolean;
  canUpdateOfferStatus: boolean;
  setSelectedOffer: (offerId: string | null) => void;
  cheatsOfferId: string | null;
  handleToggleActiveStatus: (offerId: string, active: boolean) => Promise<void>;
  handleOpenCheats: (offerId: string) => void;
  handleCloseCheats: () => void;
  handleCreateNew: () => void;
}

/**
 * Hook that manages actions, permissions, and modal state for the Progress Bars table.
 *
 * @example
 * const { handleToggleActiveStatus, handleCreateNew, isManageEnabled } = useProgressBarsActions({
 *   deleteOffer,
 *   toggleOfferStatus,
 *   refetch
 * });
 */
export const useProgressBarsActions = ({
  deleteOffer,
  toggleOfferStatus,
  refetch
}: IUseProgressBarsActionsProps): IUseProgressBarsActionsReturn => {
  const navigate = useNavigate();
  const { publisherId } = useParams();
  const { isActionEnabled: checkActionEnabled } = useUserPermissions();

  const [selectedOffer, setSelectedOffer] = useState<string | null>(null);
  const [cheatsOfferId, setCheatsOfferId] = useState<string | null>(null);

  const isManageEnabled = useMemo(
    (): boolean =>
      checkActionEnabled(
        null,
        EPermissionGroup.OFFERS,
        EPermissionAction.MANAGE,
        publisherId
      ),
    [publisherId, checkActionEnabled]
  );

  const canUpdateOfferStatus = useMemo(
    (): boolean =>
      checkActionEnabled(
        null,
        EPermissionGroup.OFFERS,
        EPermissionAction.UPDATE_OFFER_STATUS,
        publisherId
      ),
    [publisherId, checkActionEnabled]
  );

  useEffect(() => {
    if (!selectedOffer) return;
    deleteOffer.mutate(selectedOffer, {
      onSuccess: () => {
        showToast({
          message: 'Progress bar deleted successfully',
          status: EToasterStatus.SUCCESS
        });
        refetch();
      },
      onError: (e: any) => {
        showToast({
          message: 'Failed to delete progress bar',
          status: EToasterStatus.ERROR,
          description: e.response.data.message ?? ''
        });
      },
      onSettled: () => {
        setSelectedOffer(null);
      }
    });
  }, [selectedOffer]);

  const handleToggleActiveStatus = useCallback(
    async (offerId: string, active: boolean): Promise<void> => {
      const onSuccess = (): void => {
        showToast({
          message: `Progress bar ${active ? 'deactivated' : 'activated'} successfully`,
          status: EToasterStatus.SUCCESS
        });
        refetch();
      };

      const onError = (): void => {
        showToast({
          message: 'Error editing progress bar',
          status: EToasterStatus.ERROR
        });
      };

      toggleOfferStatus.mutate(
        { active: !active, offerId },
        {
          onSuccess,
          onError
        }
      );
    },
    [toggleOfferStatus, refetch]
  );

  const handleOpenCheats = useCallback((offerId: string): void => {
    setCheatsOfferId(offerId);
  }, []);

  const handleCloseCheats = useCallback((): void => {
    setCheatsOfferId(null);
  }, []);

  const handleCreateNew = useCallback((): void => {
    navigate('./form');
  }, [navigate]);

  return {
    isManageEnabled,
    canUpdateOfferStatus,
    setSelectedOffer,
    cheatsOfferId,
    handleToggleActiveStatus,
    handleOpenCheats,
    handleCloseCheats,
    handleCreateNew
  };
};
